import { Button, Dialog, Grid } from '@mui/material';
import { Project, ProjectType } from '../../../models/project';
import { useReactHookForm } from '../../../hooks/useReactHookForm';
import { ReactHookForm } from '../../../components/hook-form/react-hook-form.component';
import { useEffect, useState } from 'react';
import { NotesDialog } from '../../note/notes.dialog';
import { projectsApiKeyStore } from '../../../api/projects-api-key-store';
import { useSaveData } from '../../../hooks/useSaveData';
import * as Yup from 'yup';
import { ContactsForm } from '../project-forms/contacts.form';
import { FeaturesSetup } from '../project-forms/features-setup.component';
import { FormButtons } from '../project-forms/form-buttons.component';
import { LocationForm } from '../project-forms/location.form';
import { ProjectHeaderForm } from '../project-forms/project-header.form';
import { RecidentialCommunityForm } from '../project-forms/recidential-community.form';
import { RecidentialSingleLotForm } from '../project-forms/recidential-single-lot.form';
import { ServicesForm } from '../project-forms/services.form';
import { ProjectDocumentDialog } from '../project-document/project-document.dialog';
import { useNavigate } from 'react-router-dom';
import { ProjectInfoComponent } from '../project-forms/project-info.component';
import { Plan } from '../../../models/plan';
import { ProctorResultForm } from '../project-details/project-proctor-result/proctor-results.form';
import { Service } from '../../../models/masters/service';
import { DialogActionsBar } from '@progress/kendo-react-dialogs';
import { LoadingIndicator } from '../../../components/loading-indicator.component';
import { GuardedComponent } from '../../../components/guarded-component';
import { UserPermissionName } from '../../../models/user-permission-name';
import { useLoadData } from '../../../hooks/useLoadData';
import { ApiResponse } from '../../../models/api-response';
import { servicesApiKeyStore } from '../../../api/masters/services-api-key-store';
import { useSnackbar } from '../../../hooks/use-snackbar.hook';
import { parseErrorMessageRecursive } from '../../../utils/yup-error-parser.util';
import { useAuthorize } from '../../../hooks/use-authorize';
import dayjs from 'dayjs';

interface EditProjectDetailsTabProps {
  oldProject: any;
  pageAction: string;
  parentLoading?: boolean;
  showTab: (tab: 'details' | 'buildings') => void;
  refetchProjectDetails: () => void;
}

const projectValidationSchema = Yup.object().shape({
  customer: Yup.object().shape({ customerID: Yup.string().required('Customer is required') }),
  office: Yup.object().shape({ officeID: Yup.string().required('Office is required') }),
  // street: Yup.string().required('Street address is required'),
  projectName: Yup.string().required('Project name is required').max(21, 'Project name cannot exceed 21 characters.'),
  ffServiceCat: Yup.string().required('Fastfield service cat is required'),
  projectDivisions: Yup.array().required('Division is required').min(1, 'Division is required'),
  zipcode: Yup.string()
    .matches(/(^$|^\d{5}$|^\d{9}$)/, { message: 'Invalid zip code' })
    .nullable(),
  departments: Yup.object()
    .shape({
      departmentID: Yup.string()
        .nullable()
        .required('Department is required')
        .test('not-zero', 'Department is required', (value) => value !== '' && value !== '0'),
    })
    .required('Department is required'),
});

export const EditProjectDetailsTab = ({ oldProject, parentLoading, pageAction, showTab, refetchProjectDetails }: EditProjectDetailsTabProps) => {
  const [initilaLProjectService, setInitialProjectService] = useState(oldProject?.projectServices || []);
  // const isUATEnable = process.env.REACT_APP_UAT_FLAG === 'true';
  const navigate = useNavigate();
  const form = useReactHookForm({
    yupSchema: projectValidationSchema,
    defaultValues: {
      ...oldProject,
      customer: { customerID: oldProject.customerID, customerName: '' },
      office: { officeID: oldProject.officeID, officeName: '' },
      projectCommunityType: oldProject?.projectCommunityType ?? '',
      projectLotType: oldProject?.projectLotType ?? '',
      singleLotInfo: oldProject?.singleLotInfo ?? '',
      multipleLotsCount: oldProject?.multipleLotsCount ?? '',
      state: { stateID: oldProject?.state ? oldProject?.state : 0, stateName: '' },
      rebates: oldProject?.rebates ? oldProject?.rebates : '',
      ffServiceCat: !oldProject?.ffServiceCat ? '' : oldProject?.ffServiceCat,
      rebateCompanies: {
        utilRebateCompanyID: oldProject?.rebateCompanies[0]?.utilRebateCompanyID ?? '',
        utilRebateCompanyName: oldProject?.rebateCompanies[0]?.utilRebateCompanyName ?? '',
      },
      departments: Number(oldProject?.departmentID) ? {
        departmentID: oldProject?.departmentID ?? 0,
        departmentName: oldProject?.departmentName ?? '',
      } : null,
      sendEmail: [],
      projectCoordinators: oldProject.projectCoordinators?.map((p: any) => ({ ...p, userID: p.coordinatorID })),
      syncWithDux: !oldProject?.syncWithDux ? false : true,
      sendInsReport: false,
      includeHVACGrading: oldProject.includeHVACGrading ?? false
    },
  });
  const [projectProps, setProjectProps] = useState({
    customerID: oldProject.customerID,
    officeID: oldProject.officeID,
    projectID: oldProject.projectID,
    planTempEntityID: '',
  });
  const [rebateValue, setRebateValue] = useState<string>(oldProject?.rebates ? oldProject?.rebates : '');
  const [isCustomerLoading, setCustomerLoading] = useState<boolean>(false);
  const [editItem, setEditItem] = useState<Partial<any>>();
  const [openNoteDialog, setOpenNoteDialog] = useState(false);
  const [openPlanDialog, setOpenPlanDialog] = useState(false);
  const [editedProjectData, setEditedProjectData] = useState({});
  const [officeChange, setOfficeChange] = useState<boolean>(oldProject?.officeID ? true : false);
  const [openProjectDocumentDialog, setOpenProjectDocumentDialog] = useState(false);
  const [tempEntityID] = useState<number>();
  const [noteTempEntityID, setNoteTempEntityID] = useState<number>();
  const [planTempEntityID, setPlanTempEntityID] = useState<number>();
  const [openDialog, setOpenDialog] = useState(false);
  const [projectContactList, setProjectContactList] = useState<any[]>([]);

  const { isLoading, mutateAsync } = useSaveData<Project>(
    oldProject?.projectID ? projectsApiKeyStore.updateProject() : projectsApiKeyStore.createProject()
  );

  const handleOpenNoteDialog = () => {
    setOpenNoteDialog(!openNoteDialog);
  };

  const handleOpenPlanDialog = () => {
    setOpenPlanDialog(!openPlanDialog);
  };

  const handleOpenProjectDocumentDialog = () => {
    setOpenProjectDocumentDialog(!openProjectDocumentDialog);
  };

  const handleCloseNoteDialog = (tempProjectEntityID?: number) => {
    setOpenNoteDialog(!openNoteDialog);
    if (tempProjectEntityID) {
      setNoteTempEntityID(tempProjectEntityID);
    }
  };

  const handleClosePlanDialog = () => {
    setOpenPlanDialog(!openPlanDialog);
  };

  const handleCloseProjectDocumentDialog = (tempDocumentEntityID?: number) => {
    setOpenProjectDocumentDialog(!openProjectDocumentDialog);
    if (tempDocumentEntityID) {
      setNoteTempEntityID(tempDocumentEntityID);
    }
  };

  // const compareServiceLists = (list1: Service[], list2: Service[]): boolean => {
  //   const ids1 = list1.map((service) => service.serviceID);
  //   const ids2 = list2.map((service) => service.serviceID);

  //   if (ids1.length !== ids2.length) return false;

  //   return ids1.every((id) => ids2.includes(id)) && ids2.every((id) => ids1.includes(id));
  // };

  const compareServiceLists = (list1: Service[], list2: Service[]): boolean => {
    const ids1 = new Set(list1.map((service) => service.serviceID));
    const ids2 = new Set(list2.map((service) => service.serviceID));

    // Convert the Set to an array before iterating
    const ids2Array = Array.from(ids2);

    for (const id of ids2Array) {
      if (!ids1.has(id)) {
        return false; // New service added
      }
    }

    return true; // No new service added
  };

  const { data } = useLoadData<ApiResponse<Service[]>>(
    servicesApiKeyStore.getServiceMappingList(
      0,
      !oldProject?.projectID ? form.getValues('office.officeID') : 0,
      !oldProject?.projectID ? 0 : oldProject?.projectID,
      0,
      0
    )
  );

  useEffect(() => {
    if (data && oldProject?.projectID) {
      data?.data?.map((service: Service) => {
        if (service.serviceCode === 'Dry Run') {
          form.setValue('dryRunFee', service.price);
        } else if (service.serviceCode === 'Late Cancellation') {
          form.setValue('lateCancellationFee', service.price);
        } else if (service.serviceCode === 'Trip Charge') {
          form.setValue('tripCharge', service.price);
        }
      });
      // Toggle state to trigger re-render
    }
  }, [data, projectProps?.officeID]);

  const formatDate = (date: any) => {
    if (!date) return ''; // Handle null or undefined dates
    return dayjs(date).format('YYYY-MM-DD'); // Format as YYYY-MM-DD
  };

  const areArraysEqual = (arr1: any, arr2: any) => {
    if (arr1.length !== arr2.length) return false;

    const sorted1 = [...arr1].sort();
    const sorted2 = [...arr2].sort();

    return sorted1.every((value, index) => value === sorted2[index]);
  };

  const checkServiceDuplicate = (services: any) => {
    const deduplicatedServices = services?.reduce((acc: any, current: any) => {
      if (!acc?.map?.has(current?.serviceID)) {
        // Clone current to avoid mutating original
        const newService: any = { ...current };

        // If isNotReadyService is true, set isActive to true
        if (newService?.isNotReadyService) {
          newService.isActive = true;
        }

        acc?.map?.set(newService?.serviceID, true);
        acc?.result?.push(newService);
      }
      return acc;
    }, { map: new Map(), result: [] }).result;

    return deduplicatedServices;
  };


  const handleSubmit = async (project: any) => {
    // console.log(56, project.planReviews?.map((p: any) => p.planReviewTypeID));
    const filterData = data?.data
      ?.filter((d) => d.isNotReadyService)
      .map((service: Service) => {
        if (service.serviceCode === 'Dry Run') {
          return {
            ...service,
            price: project?.dryRunFee,
            invoiceRetestAmount: project?.dryRunFee,
            accrualAmount: project?.dryRunFee,
            accrualRetestAmount: project?.dryRunFee,
          };
        } else if (service.serviceCode === 'Late Cancellation') {
          return {
            ...service,
            price: project?.lateCancellationFee,
            invoiceRetestAmount: project?.lateCancellationFee,
            accrualAmount: project?.lateCancellationFee,
            accrualRetestAmount: project?.lateCancellationFee,
          };
        } else if (service.serviceCode === 'Trip Charge') {
          return {
            ...service,
            price: project?.tripCharge,
            invoiceRetestAmount: project?.tripCharge,
            accrualAmount: project?.tripCharge,
            accrualRetestAmount: project?.tripCharge,
          };
        }
        return service; // Return the service unchanged if no condition matches
      });

    const newData = {
      ...project,
      ...(planTempEntityID && { planTempEntityID }),
      customerID: project.customer?.customerID,
      officeID: project.office?.officeID,
      state: project.state?.stateID,
      projectPlans: project.projectPlans?.map((m: any) => m.planID),
      multipleLotsCount: Number(project.multipleLotsCount),
      projectMunicipalities: project.projectMunicipalities?.map((m: any) => m.municipalityID),
      marketAreas: project.marketAreas?.map((m: any) => m.marketAreaID),
      planReviews: project.planReviews?.map((p: any) => p.planReviewTypeID),
      projectDivisions: project.projectDivisions?.map((m: any) => m.divisionID),
      projectContacts: project.projectContacts?.map((m: any) => m.contactsID),
      forecastStartDate: formatDate(project?.forecastStartDate),
      generateWorkOrders: project?.buttonType == 'with-workorder',

      projectDivisionInfo:
        project?.sendEmail?.length > 0
          ? project?.sendEmail?.map((id: any) => ({
            divisionID: id,
            sendReminderEmail: true,
          }))
          : [],
      projectContactsList:
        projectContactList?.length > 0
          ? projectContactList?.map((p: any) => {
            return {
              contactsID: p.contactsID,
              syncWithDux: project?.syncWithDux ? p.syncWithDux : false,
              isFastFieldCM: p?.isFastFieldCM ?? false,
            };
          })
          : project?.projectContacts?.map((p: any) => {
            return {
              contactsID: p.contactsID,
              syncWithDux: project?.syncWithDux ? p.syncWithDux : false,
              isFastFieldCM: p?.isFastFieldCM ?? false,
            };
          }),
      projectProctorResults: project?.projectProctorResults?.map((pr: any) => pr.proctorResultsID),
      projectCoordinators: project.projectCoordinators?.map((p: any) => p.userID),
      projectSubTypes: project.projectSubTypes?.map((ps: any) => ps.projectSubTypeID),
      projectServices: checkServiceDuplicate([...(project?.projectServices || []), ...(filterData || [])]),
      rebateCompanies: project?.rebateCompanies?.utilRebateCompanyID
        ? [project?.rebateCompanies?.utilRebateCompanyID]
        : [],
      projectBuildings: project?.projectBuildings?.map((b: any) => ({
        ...b,
        buildingFloors: b.buildingFloors.map((f: any) => ({ ...f, buildingName: b.buildingName })),
        buildingCommonAreas: b.buildingCommonAreas.map((c: any) => ({ ...c, buildingName: b.buildingName })),
      })),
      tempEntityID,
      noteTempEntityID,
      isCityChanged: oldProject?.city !== project?.city,
      isStateChanged: oldProject?.state !== project?.state?.stateID,
      isZipChanged: oldProject?.zipcode !== project?.zipcode,
      isMarketChanged: !areArraysEqual(
        project?.marketAreas?.map((m: any) => m.marketAreaID),
        oldProject?.marketAreas?.map((m: any) => m.marketAreaID)
      ),
      isMunicipalityChanged: !areArraysEqual(
        project?.projectMunicipalities?.map((m: any) => m.municipalityID),
        oldProject?.projectMunicipalities?.map((m: any) => m.municipalityID)
      ),
      isCountyChanged: oldProject?.county !== project?.county,
      isRebatesChanged: oldProject?.rebates !== project?.rebates,
      servicesForWOCreation: project?.projectServices
        ?.filter((item: any) => item?.createWO === true)
        ?.map((item: any) => item?.serviceID),
      includeHVACGrading: project?.includeHVACGrading ?? false,
      departmentID: project?.departments?.departmentID ?? 0
      // ...(isUATEnable && {
      //   departmentID: project?.departments?.departmentID ?? 0
      // }),
    };

    if (newData?.projectServices?.length > 0 && newData?.servicesForWOCreation?.length == 0 && project?.buttonType == 'with-workorder') {
      showSnackbar(`Please select at least one 'Create Work Order' before saving.`, 'error');
      return
    }

    if (newData?.servicesForWOCreation?.length > 0 && project?.buttonType == 'without-workorder') {
      showSnackbar(`Please do not select 'Create Work Order' before saving.`, 'error');
      return
    }

    const projectServices = form?.getValues('projectServices');

    // Ensure projectServices is an array before using .some()
    const hasCondition =
      Array.isArray(projectServices) &&
      projectServices?.some((service: any) => service?.lotwithoutWorkOrderExists && service?.mappingID !== 0);

    const hasChanged = !compareServiceLists(initilaLProjectService, form?.getValues('projectServices')) || hasCondition;
    // projectService?.dataItem?.lotwithoutWorkOrderExists && projectService?.dataItem?.mappingID !== 0 &&
    if (hasChanged) {
      // setOpenDialog(true);
      callProjectApi({ ...newData, IsServiceListModified: true });
    } else {
      callProjectApi({ ...newData, IsServiceListModified: false });
    }

    // if (
    //   project.projectType === ProjectType.TimeAndMaterial ||
    //   project.projectLotType === ProjectType.CommunityMultipleLot ||
    //   project.projectLotType === ProjectType.Buildings ||
    //   project.projectCommunityType === ProjectType.Buildings
    // ) {
    //   showTab('buildings');
    // }
  };

  const callProjectApi = async (data: any) => {
    await mutateAsync({
      data,
    });

    navigate(`/projects/details/${oldProject?.projectID}`);
  };

  const handleProjectProps = (type: string, value: any) => {
    if (type == 'officeID' && value != null) setOfficeChange(true);
    else setOfficeChange(false);
    setProjectProps((prevState) => ({
      ...prevState,
      [type]: value,
    }));
  };

  const handleCustomerLoading = (value: boolean) => {
    setCustomerLoading(value);
  };

  const handleRebateValue = (rebate: string) => {
    setRebateValue(rebate);
  };

  const closeDialog = () => {
    callProjectApi({ ...editedProjectData, IsServiceListModified: false });
    setOpenDialog(false);
  };

  const handleSumbitConfirm = async () => {
    callProjectApi(editedProjectData);
    setOpenDialog(false);
  };

  const handleProjectContactList = (projectContacts: any[]) => {
    setProjectContactList(projectContacts ?? []);
  };

  const { showSnackbar } = useSnackbar();

  const handleError = (error: any) => {
    const messages = parseErrorMessageRecursive(error);

    let requiredMessages: any[] = [];
    let otherMessages: any[] = [];

    // Classify messages
    Array.isArray(messages) &&
      messages?.forEach((message) => {
        if (message.endsWith('is required')) {
          requiredMessages.push(message?.split(' is required')[0]?.trim());
        } else {
          otherMessages.push(message.trim());
        }
      });

    // Remove duplicates
    requiredMessages = Array.from(new Set(requiredMessages));
    otherMessages = Array.from(new Set(otherMessages));

    // Construct the final message
    const finalRequiredMessage = requiredMessages.length > 0 ? `${requiredMessages?.join(', ')} is required` : '';
    const finalOtherMessage = otherMessages?.join(', ');

    // Combine messages
    const finalMessage = [finalRequiredMessage, finalOtherMessage]?.filter(Boolean)?.join(', ');

    // Show the final formatted message
    showSnackbar(`Validation Error: ${finalMessage}`, 'error');
  };

  const handleFormSubmit = (buttonType: string) =>
    form.handleSubmit(
      (values) => handleSubmit({ ...values, buttonType }),
      handleError
    )();

  const isReadOnlyPermission = useAuthorize([UserPermissionName?.READ_ONLY]);

  return (
    <>
      <ReactHookForm formName="PROJECT_DETAILS_FORM" onSubmit={() => { }} formContextProps={form}>
        <Grid container spacing={1.5}>
          <Grid item lg={12} sm={12} xl={12} xs={12}>
            <ProjectHeaderForm
              changeProps={handleProjectProps}
              onCustomerLoading={handleCustomerLoading}
              onRebateValue={handleRebateValue}
              isEdit
            />
          </Grid>
          <Grid item lg={12} sm={12} xl={12} xs={12}>
            <ProjectInfoComponent isEdit project={oldProject} rebateValue={rebateValue} />
          </Grid>

          <Grid style={{ display: 'none' }} item lg={12} sm={12} xl={12} xs={12}>
            <RecidentialSingleLotForm isEdit />
          </Grid>

          {oldProject?.projectCommunityType === ProjectType?.CommunityScatteredLot ? null : (
            <Grid item lg={12} sm={12} xl={12} xs={12}>
              <RecidentialCommunityForm isEdit />
            </Grid>
          )}

          <Grid item lg={12} sm={12} xl={12} xs={12}>
            <LocationForm stateID={oldProject?.state} project={oldProject} isEdit />
          </Grid>
          <GuardedComponent requiredPermissions={[UserPermissionName.VIEW_SERVICE_PROJECT]}>
            <Grid item lg={12} sm={12} xl={12} xs={12}>
              <ServicesForm
                name="projectServices"
                id={oldProject?.projectID}
                refetchProjectDetails={refetchProjectDetails}
                project={oldProject}
                parentLoading={parentLoading}
                page="Project"
                pageAction={pageAction}
              />
            </Grid>
          </GuardedComponent>
          <GuardedComponent requiredPermissions={[UserPermissionName.VIEW_CONTACT]}>
            <Grid item lg={12} sm={12} xl={12} xs={12}>
              <ContactsForm
                name="projectContacts"
                label="Contacts"
                page="edit"
                updateContactList={handleProjectContactList}
              />
            </Grid>
          </GuardedComponent>
          <Grid item lg={12} sm={12} xl={12} xs={12}>
            <ProctorResultForm name="projectProctorResults" label="Proctor Results" projectID={oldProject?.projectID} />
          </Grid>
          <Grid item lg={12} sm={12} xl={12} xs={12}>
            <FeaturesSetup
              openNoteDialog={handleOpenNoteDialog}
              openPlanDialog={handleOpenPlanDialog}
              openProjectDocumentDialog={handleOpenProjectDocumentDialog}
            />
          </Grid>
          {isLoading && <LoadingIndicator isLoading={isLoading} />}
          <Grid item lg={12} sm={12} xl={12} xs={12}>
            <FormButtons
              isLoading={isLoading}
              page="edit"
              handleCancel={() => {
                navigate(`/projects/details/${oldProject?.projectID}`);
              }}
              handleSubmit={handleFormSubmit}
            />
          </Grid>
        </Grid>
      </ReactHookForm>
      <NotesDialog
        title="PROJECT"
        noteRequestArgs={{ ...(oldProject ? { projectID: oldProject?.projectID } : { noteTempEntityID }) }}
        open={openNoteDialog}
        onClose={handleCloseNoteDialog}
      />
      <ProjectDocumentDialog
        open={openProjectDocumentDialog}
        documentRequestArgs={{ projectID: oldProject?.projectID }}
        onClose={handleCloseProjectDocumentDialog}
      />
      {openDialog && (
        <Dialog
          open={openDialog}
          className="modalpopup"
          title={'CONFIRM STATUS'}
        // onClose={closeDialog}
        >
          <div>
            <div style={{ padding: '25px 50px' }}>
              <div>Are you sure you want to generate remaining work orders for all services?</div>
              {/* <LoadingIndicator isLoading={isLoadingUnitComplete} /> */}
            </div>
          </div>
          <DialogActionsBar>
            {isReadOnlyPermission && (
              <Button style={{ maxWidth: '140px' }} size='small' type="button" variant="outlined" onClick={() => setOpenDialog(false)}>
                Cancel
              </Button>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', padding: 2 }} className="  ">
              <button
                className="btn btn-sm btn-primary btn-lightGrey"
                style={{
                  backgroundColor: isReadOnlyPermission ? '#d7dbdd' : '#0000b5',
                  color: isReadOnlyPermission ? 'black' : '#fff',
                  cursor: 'pointer', padding: 2, marginRight: 6
                }}
                onClick={closeDialog}
                autoFocus
              >
                No
              </button>

              <button
                className="btn btn-sm btn-primary  btn-delete"
                style={{
                  backgroundColor: isReadOnlyPermission ? '#d7dbdd' : '#0000b5',
                  color: isReadOnlyPermission ? 'black' : '#fff',
                  cursor: 'pointer', padding: 2
                }}
                type="button"
                onClick={handleSumbitConfirm}
                disabled={isLoading || isReadOnlyPermission}
              >
                Yes
              </button>
            </div>
          </DialogActionsBar>
        </Dialog >
      )}
    </>
  );
};
